import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, ArchiveRestore, AlertTriangle } from "lucide-react";
import ListFilter from "@/components/admin/ListFilter";

/**
 * TrashPanel — admin view of soft-deleted blogs. Powered by
 * GET /api/admin/blogs/trash. Two per-row actions:
 *   • Restore   → POST /api/blogs/{id}/restore  (undoes the soft delete)
 *   • Purge     → DELETE /api/blogs/{id}/purge  (permanent, requires confirm)
 *
 * The Undo toast in UploadPanel handles the first 8 seconds after a delete;
 * anything past that window lives here.
 */
export default function TrashPanel({ refresh }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blogs/trash");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load trash");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (blog) => {
    setBusyId(blog.blog_id);
    try {
      await api.post(`/blogs/${encodeURIComponent(blog.blog_id)}/restore`);
      toast.success(`Restored “${blog.title}”`);
      setRows(prev => prev.filter(r => r.blog_id !== blog.blog_id));
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setBusyId(null); }
  };

  const purge = async (blog) => {
    // Two-step confirm — the row must be armed before the delete fires.
    const armed = confirmArmed.get(blog.blog_id);
    if (!armed) {
      setConfirmArmed(new Map(confirmArmed).set(blog.blog_id, Date.now()));
      // Auto-disarm after 6s if the admin walks away.
      setTimeout(() => {
        setConfirmArmed(prev => {
          const next = new Map(prev);
          next.delete(blog.blog_id);
          return next;
        });
      }, 6000);
      return;
    }
    setBusyId(blog.blog_id);
    try {
      await api.delete(`/blogs/${encodeURIComponent(blog.blog_id)}/purge`);
      toast.success(`Permanently deleted “${blog.title}”`);
      setRows(prev => prev.filter(r => r.blog_id !== blog.blog_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setBusyId(null);
      setConfirmArmed(prev => {
        const next = new Map(prev);
        next.delete(blog.blog_id);
        return next;
      });
    }
  };
  const [confirmArmed, setConfirmArmed] = useState(new Map());

  const filtered = rows.filter(b => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (b.title || "").toLowerCase().includes(q)
        || (b.blog_id || "").toLowerCase().includes(q)
        || (b.author_name || "").toLowerCase().includes(q);
  });

  const formatWhen = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <Card data-testid="admin-trash-panel">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-slate-500"/> Trash
            <span className="text-sm font-normal text-slate-500">({rows.length})</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <ListFilter value={filter} onChange={setFilter}
                        placeholder="Filter by title, ID, or author…"
                        testId="admin-trash-filter"/>
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-trash-refresh">
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Soft-deleted blogs — restore them to bring them back, or permanently delete to free the Blog ID and Slug.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm italic text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-trash-empty" className="text-sm italic text-slate-500">
            {rows.length === 0 ? "Trash is empty. Deleted blogs will appear here." : "No results for that filter."}
          </p>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800" data-testid="admin-trash-list">
            {filtered.map(blog => {
              const armed = confirmArmed.has(blog.blog_id);
              return (
                <li key={blog.blog_id}
                    data-testid={`admin-trash-row-${blog.blog_id}`}
                    className="py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                      {blog.title || "Untitled"}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest">
                      <span className="font-mono bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded">
                        {blog.blog_id}
                      </span>
                      <span className="text-slate-500 normal-case">deleted {formatWhen(blog.deleted_at)}</span>
                      {blog.author_name && <span className="text-slate-500 normal-case">by {blog.author_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm"
                            data-testid={`admin-trash-restore-${blog.blog_id}`}
                            disabled={busyId === blog.blog_id}
                            onClick={() => restore(blog)}>
                      <ArchiveRestore className="w-4 h-4 mr-1"/> Restore
                    </Button>
                    <Button variant={armed ? "destructive" : "outline"} size="sm"
                            data-testid={`admin-trash-purge-${blog.blog_id}`}
                            disabled={busyId === blog.blog_id}
                            onClick={() => purge(blog)}
                            className={armed ? "" : "text-red-700 border-red-200 hover:bg-red-50"}>
                      {armed ? (<><AlertTriangle className="w-4 h-4 mr-1"/> Confirm permanent delete</>)
                             : (<><Trash2 className="w-4 h-4 mr-1"/> Delete forever</>)}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
